import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';

import InputLabelTooltip from '~/components/InputLabelTooltip';
import TextField from '~/components/RedesignedTextField';
import SalesforceLogo from '~/images/destinations/Salesforce.svg';
import { hasError } from '~/lib/graphql';
import { readableSingularSegmentType, segmentTypes } from '~/lib/segments';
import Disconnected from '~/pages/destinations/components/Disconnected';
import SuppressionForm from '~/pages/destinations/components/SuppressionForm';
import TriggerType from '~/pages/destinations/components/TriggerType';
import defaultTriggerTypes from '~/pages/destinations/components/TriggerType/defaultValues';
import RadioChecked from '~/pages/destinations/components/TriggerType/RadioChecked.svg';
import RadioUnchecked from '~/pages/destinations/components/TriggerType/RadioUnchecked.svg';

import sharedStyles from '../styles';
import styles from './styles';

const placeholderValue = '';

class Salesforce extends React.Component {
  state = {
    body: null,
    duplicateSuppressionTimeframe: 0,
    object: null,
    subject: null,
    triggerType: 'segment_enter',
  };

  UNSAFE_componentWillMount() {
    if (this.props.destination) {
      this.setState({
        ...this.props.destination.config,
        triggerType: this.props.destination.triggerType,
      });
    }
  }

  handleFieldChange = (name, value) => {
    this.setState({ [name]: value }, this.handleInputChange);
  };

  handleInputChange = () => {
    const { onInputChange } = this.props;

    const {
      body,
      duplicateSuppressionTimeframe,
      object,
      subject,
      triggerType,
    } = this.state;

    onInputChange({
      config: {
        body,
        duplicateSuppressionTimeframe,
        object,
        subject,
      },
      triggerType,
    });
  };

  render() {
    const { accountConnector, classes, errors, refetch, typename } = this.props;

    if (!accountConnector) {
      return (
        <Disconnected
          service="salesforce"
          onSuccess={refetch}
          Logo={SalesforceLogo}
        />
      );
    }

    const {
      object,
      triggerType,
      subject,
      body,
      duplicateSuppressionTimeframe,
    } = this.state;

    const objectValue = object || placeholderValue;
    const triggerTypeValue = triggerType || placeholderValue;

    return (
      <div>
        <h2 className={classes.subheading}>Trigger</h2>
        <span className={classes.triggerSubtext}>
          Choose when this Destination will be run.{' '}
          <a
            href="https://help.clearbit.com/hc/en-us/articles/360023453694"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'underline' }}>
            Learn more
          </a>
        </span>

        <TriggerType
          errors={errors}
          handleChange={this.handleFieldChange}
          triggerType={triggerTypeValue}
          triggerTypes={defaultTriggerTypes(typename)}
        />

        <h2 className={classes.subheading}>Configure Salesforce options</h2>

        <div className={classes.contentBox}>
          <FormControl
            className={classes.formControl}
            error={hasError(errors, 'object')}
            required>
            {hasError(errors, 'object') && (
              <FormHelperText className={classes.errorText}>
                You must choose a Salesforce object
              </FormHelperText>
            )}
            <RadioGroup
              className={classes.group}
              value={objectValue}
              onChange={(event) => {
                this.handleFieldChange('object', event.target.value);
              }}>
              <FormControlLabel
                classes={{
                  root: classes.formControlLabelRoot,
                  label: classes.formControlLabel,
                }}
                value="chatter"
                control={
                  <Radio
                    checkedIcon={<RadioChecked />}
                    icon={<RadioUnchecked />}
                    classes={{ root: classes.radioRoot }}
                  />
                }
                label={
                  <span className={classes.radioLabel}>
                    Post message to Salesforce chatter
                    <InputLabelTooltip
                      style={{ top: 0 }}
                      title={`Post to Chatter when a ${readableSingularSegmentType(
                        typename,
                      )} matches a segment.`}
                    />
                  </span>
                }
              />
              <FormControlLabel
                classes={{
                  root: classes.formControlLabelRoot,
                  label: classes.formControlLabel,
                }}
                value="task"
                control={
                  <Radio
                    checkedIcon={<RadioChecked />}
                    icon={<RadioUnchecked />}
                    classes={{ root: classes.radioRoot }}
                  />
                }
                label={
                  <span className={classes.radioLabel}>
                    Create a Salesforce Task{' '}
                    <InputLabelTooltip
                      style={{ top: 0 }}
                      title={`
                      Create a Task assigned to the ${
                        typename === segmentTypes.company
                          ? 'Account'
                          : 'Lead or Contact'
                      } Owner when a ${readableSingularSegmentType(
                        typename,
                      )} matches a segment
                      `}
                    />
                  </span>
                }
              />
            </RadioGroup>
          </FormControl>

          <hr className={classes.break} style={{ marginTop: 24 }} />

          <div className={classes.formContainer}>
            {objectValue === 'task' && (
              <TextField
                className={classes.field}
                error={hasError(errors, 'subject')}
                fullWidth
                defaultValue={subject}
                label="Task Subject"
                onChange={(event) => {
                  this.handleFieldChange('subject', event.target.value);
                }}
                variant="standard"
                placeholder="Enter task subject"
              />
            )}

            <TextField
              className={classes.field}
              error={hasError(errors, 'body')}
              fullWidth
              multiline
              rows="8"
              defaultValue={body}
              label="Message body"
              onChange={(event) => {
                this.handleFieldChange('body', event.target.value);
              }}
              variant="standard"
              placeholder="Enter text for the message"
            />

            <span className={classes.markdownCopy}>
              Markdown tags are supported.{' '}
              <a
                className={classes.link}
                target="_blank"
                rel="noopener noreferrer"
                href="https://help.clearbit.com/hc/en-us/articles/360022534154-Liquid-Templating">
                Learn more
              </a>
            </span>
          </div>

          <hr className={classes.break} />

          <SuppressionForm
            exclusion_timeframe={duplicateSuppressionTimeframe}
            typename={readableSingularSegmentType(typename)}
            onChange={({ exclusion_timeframe }) => {
              this.handleFieldChange(
                'duplicateSuppressionTimeframe',
                exclusion_timeframe,
              );
            }}
          />
        </div>
      </div>
    );
  }
}

Salesforce.propTypes = {
  accountConnector: PropTypes.object,
  classes: PropTypes.object.isRequired,
  destination: PropTypes.object,
  errors: PropTypes.array.isRequired,
  onInputChange: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
  typename: PropTypes.string.isRequired,
};

export default withStyles(
  (theme) => ({
    ...sharedStyles(theme),
    ...styles(theme),
  }),
  { name: 'SegmentDestinationNewPageSalesforce' },
)(Salesforce);
